import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutDashboard, Target, Calendar, Package, Users, Settings, X, ShieldAlert, Factory } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isNiche } from '../lib/niche';

export const CommandPalette: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const { user, company, isSuperAdmin } = useAuth();
    const isAcm = isNiche(company, 'acm');
    
    const isManager = user?.role === 'gestor' || user?.role === 'ceo';

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(true);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        } else {
            setQuery('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const routes = [
        { name: 'Dashboard', path: '/', icon: LayoutDashboard },
        { name: 'Leads (CRM)', path: '/leads', icon: Target },
        { name: 'Agenda', path: '/calendar', icon: Calendar },
    ];

    if (isManager || isSuperAdmin) {
        routes.push({ name: 'Estoque / Insumos', path: isAcm ? '/estimates?tab=inventory' : '/inventory', icon: Package });
        if (isAcm) routes.push({ name: 'Painel de Produção', path: '/producao', icon: Factory });
        routes.push({ name: 'Usuários da Equipe', path: '/users', icon: Users });
        routes.push({ name: 'Configurações', path: '/settings', icon: Settings });
    }

    if (isSuperAdmin) {
        routes.push({ name: 'Admin Global', path: '/superadmin', icon: ShieldAlert });
    }

    const filteredRoutes = routes.filter(route => 
        route.name.toLowerCase().includes(query.toLowerCase())
    );

    const handleSelect = (path: string) => {
        navigate(path);
        setIsOpen(false);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]">
            <div 
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" 
                onClick={() => setIsOpen(false)}
            />
            <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                    <Search className="w-5 h-5 text-slate-500" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="flex-1 px-3 py-2 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none text-lg"
                        placeholder="Buscar ou ir para... (Ex: leads)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <button aria-label="Fechar" 
                        onClick={() => setIsOpen(false)}
                        className="p-1 rounded-md text-slate-500 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="max-h-[60vh] overflow-y-auto p-2">
                    {filteredRoutes.length > 0 ? (
                        <div>
                            <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Páginas
                            </div>
                            {filteredRoutes.map((route, idx) => {
                                const Icon = route.icon;
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => handleSelect(route.path)}
                                        className="w-full flex items-center px-3 py-3 text-sm rounded-xl text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    >
                                        <Icon className="w-5 h-5 mr-3 opacity-70" />
                                        {route.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="px-4 py-8 text-center text-slate-500">
                            Nenhum resultado encontrado para "{query}"
                        </div>
                    )}
                </div>
                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
                    <span>Use as setas para navegar e Enter para selecionar (Em breve)</span>
                    <span className="flex items-center gap-1"><kbd className="bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-600 font-mono">ESC</kbd> para fechar</span>
                </div>
            </div>
        </div>
    );
};
